import { useState, type ReactElement, type ReactNode } from "react";
import { Modal } from "./Modal";

type Tone = "warning" | "danger";

interface BaseProps {
  open: boolean;
  title: string;
  description: ReactNode;
  /** Icon class without the "ti-" prefix. Falls back to a sensible default. */
  icon?: string;
  /** Confirm button label. */
  confirmLabel: string;
  /** Visual tone of the confirm button + icon ring. */
  tone?: Tone;
  busy?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

interface SimpleProps extends BaseProps {
  variant?: "simple";
}

interface TypeToConfirmProps extends BaseProps {
  variant: "type-to-confirm";
  /** Word the user must type verbatim to enable the confirm button. */
  expected: string;
  /** Optional label above the text input (default: "Type X to confirm"). */
  promptLabel?: string;
}

export type ConfirmDialogProps = SimpleProps | TypeToConfirmProps;

interface Resolved {
  icon: string;
  buttonClass: string;
  requireTyping: boolean;
  expected: string;
  promptLabel: string | null;
  stringDescription: string | null;
}

function resolveProps(props: Readonly<ConfirmDialogProps>): Resolved {
  const tone: Tone = props.tone ?? "warning";
  const icon = props.icon ?? (tone === "danger" ? "ti-trash" : "ti-alert-triangle");
  const buttonClass = tone === "danger" ? "btn btn-danger" : "btn btn-warning";
  const requireTyping = props.variant === "type-to-confirm";
  const expected = props.variant === "type-to-confirm" ? props.expected : "";
  const promptLabel =
    props.variant === "type-to-confirm"
      ? (props.promptLabel ?? `Type ${props.expected} to confirm`)
      : null;
  const stringDescription = typeof props.description === "string" ? props.description : null;
  return { icon, buttonClass, requireTyping, expected, promptLabel, stringDescription };
}

export function ConfirmDialog(props: Readonly<ConfirmDialogProps>): ReactElement {
  const resolved = resolveProps(props);
  const [typed, setTyped] = useState("");
  const matches = !resolved.requireTyping || typed === resolved.expected;
  const disabled = !matches || (props.busy ?? false);
  function handleClose(): void {
    setTyped("");
    props.onCancel();
  }
  function handleConfirm(): void {
    setTyped("");
    props.onConfirm();
  }
  return (
    <ConfirmModal
      props={props}
      resolved={resolved}
      typed={typed}
      onTyped={setTyped}
      disabled={disabled}
      onClose={handleClose}
      onConfirm={handleConfirm}
    />
  );
}

interface ConfirmModalProps {
  props: Readonly<ConfirmDialogProps>;
  resolved: Resolved;
  typed: string;
  onTyped: (s: string) => void;
  disabled: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

function ConfirmModal({
  props,
  resolved,
  typed,
  onTyped,
  disabled,
  onClose,
  onConfirm,
}: Readonly<ConfirmModalProps>): ReactElement {
  const { open, title, description, confirmLabel, busy } = props;
  const tone: Tone = props.tone ?? "warning";
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      icon={resolved.icon}
      iconTone={tone}
      {...(resolved.stringDescription !== null ? { description: resolved.stringDescription } : {})}
      footer={
        <Footer
          confirmLabel={confirmLabel}
          buttonClass={resolved.buttonClass}
          disabled={disabled}
          busy={busy ?? false}
          onCancel={onClose}
          onConfirm={onConfirm}
        />
      }
    >
      {typeof description !== "string" && <div className="text-sm">{description}</div>}
      {resolved.requireTyping && resolved.promptLabel !== null && (
        <TypeToConfirmInput
          expected={resolved.expected}
          promptLabel={resolved.promptLabel}
          value={typed}
          onChange={onTyped}
        />
      )}
    </Modal>
  );
}

interface FooterProps {
  confirmLabel: string;
  buttonClass: string;
  disabled: boolean;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

function Footer({
  confirmLabel,
  buttonClass,
  disabled,
  busy,
  onCancel,
  onConfirm,
}: Readonly<FooterProps>): ReactElement {
  return (
    <>
      <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={busy}>
        Cancel
      </button>
      <button type="button" className={buttonClass} onClick={onConfirm} disabled={disabled}>
        {busy ? "Working..." : confirmLabel}
      </button>
    </>
  );
}

interface TypeInputProps {
  expected: string;
  promptLabel: string;
  value: string;
  onChange: (v: string) => void;
}

function TypeToConfirmInput({
  expected,
  promptLabel,
  value,
  onChange,
}: Readonly<TypeInputProps>): ReactElement {
  return (
    <div className="mt-3 rounded-ctl bg-surface-2 p-3">
      <div className="mb-1.5 text-xs text-ink-secondary">
        {promptLabel} <span className="font-mono font-medium text-ink">{expected}</span>
      </div>
      <input
        className="input font-mono"
        type="text"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
        }}
        placeholder={expected}
        autoFocus
        autoComplete="off"
        spellCheck={false}
      />
    </div>
  );
}
